'use client';

import React from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { ChevronDown, Plus, X } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useMonitoring } from '@kit/monitoring/hooks';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { useVerifyPhoneOtp } from '@kit/supabase/hooks/use-verify-phone-otp';
import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@kit/ui/input-otp';
import { PhoneInput, isValidPhoneNumber } from '@kit/ui/phone-input';
import { toast } from '@kit/ui/sonner';
import { Spinner } from '@kit/ui/spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kit/ui/tooltip';
import { Trans } from '@kit/ui/trans';

import { useCaptcha } from '../captcha/client';
import { useLastAuthMethod } from '../hooks/use-last-auth-method';
import {
  PhoneSignInSchema,
  PhoneSignupProfileSchema,
  PhoneOtpVerificationSchema,
  formatPhoneNumber,
} from '../schemas/phone-auth.schema';
import type { SignupCustomField } from '../schemas/signup-custom-fields.schema';
import { associateCustomerWithTenantAction } from '../server/associate-customer-action';
import { completePhoneSignupProfileAction } from '../server/complete-phone-signup-profile-action';
import {
  identifyAccountByPhoneAction,
  type IdentifyPhoneResult,
} from '../server/identify-phone-action';
import { prepareAndSendPhoneOtpAction } from '../server/prepare-and-send-phone-otp';
import { AuthErrorAlert } from './auth-error-alert';
import {
  SignupCustomFieldsInput,
  refineSignupFields,
  signupProfileSetupPath,
} from './signup-custom-fields-input';
import { SignupEmailVerifyStep } from './signup-email-verify-step';
import { TermsAndConditionsFormField } from './terms-and-conditions-form-field';
import { UnknownAccountCard } from './unknown-account-card';

const ASSURANCE_LEVEL_2 = 'aal2';
const MAX_CHILDREN = 5;

type PhoneSignInContainerProps = {
  shouldCreateUser: boolean;
  inviteToken?: string;
  defaultPhone?: string;
  // Pre-fill name on the profile step when known (e.g. arriving from the
  // sign-in identify flow with a guest_upgrade name on file). Only used
  // when shouldCreateUser=true.
  defaultName?: string;
  // Masked email hint (e.g. "k•••@g•••.com") from a guest_upgrade
  // identify lookup. Shown above the email field on the profile step so
  // the user knows which email is on file. Only used when shouldCreateUser=true.
  emailHint?: string;
  displayTermsCheckbox?: boolean;
  signupCustomFields?: SignupCustomField[];
  displayChildrenSection?: boolean;
  // Path to send unknown / guest-upgrade phones to. Mirrors the email flow's
  // PasswordSignInContainer.signUpPath. Only used when shouldCreateUser=false.
  signUpPath?: string;
  // Provided by the sign-in methods container when an email method is
  // enabled — the unknown-account card's "try email instead" action
  // (native parity). Only used when shouldCreateUser=false.
  onSwitchToEmail?: () => void;
  // Slug of the tenant the user is currently on. Forwarded to
  // useSignInWithPhone so the Send-SMS hook can resolve tenant branding.
  tenantSlug?: string;
  // Rendered below the FIRST screen only (the phone-number step) — the
  // methods container passes its alternative-methods section here so the
  // switcher disappears once the user has progressed into the flow.
  footer?: React.ReactNode;
};

// Step state machine:
//   'phone'       — phone-only input (first screen)
//   'otp'         — OTP code entry
//   'profile'     — name + email + children (sign-up only, after OTP verified)
//   'verifyEmail' — skippable code entry for the optional email just stored
type Step = 'phone' | 'otp' | 'profile' | 'verifyEmail';

export function PhoneSignInContainer(props: PhoneSignInContainerProps) {
  const verifyMutation = useVerifyPhoneOtp();
  const supabase = useSupabase();
  const { recordAuthMethod } = useLastAuthMethod();
  const params = useSearchParams();
  const { t } = useTranslation();

  const shouldCreateUser = props.shouldCreateUser;

  const [step, setStep] = React.useState<Step>('phone');
  const [verifiedPhone, setVerifiedPhone] = React.useState('');
  // Email stored by the profile step, pending the skippable verify step.
  const [pendingVerifyEmail, setPendingVerifyEmail] = React.useState('');
  const [resendCooldown, setResendCooldown] = React.useState(0);
  // Whether the Send-SMS hook will ALSO mirror the code to the account's
  // VERIFIED email (from identifyAccountByPhoneAction) — drives the
  // "WhatsApp and email" vs "WhatsApp" copy on the code screen.
  const [otpEmailMirror, setOtpEmailMirror] = React.useState(false);
  // One-shot guard for the guest-upgrade auto-send, owned by the CONTAINER so it
  // survives PhoneNumberForm unmount/remount (the form unmounts when we move to
  // the OTP step; "Request new code" remounts it). A per-mount ref inside the
  // form would reset and auto-send again, bypassing the resend cooldown and
  // double-sending. The form resets this to false if its own auto-send fails so
  // the user can retry.
  const autoSentRef = React.useRef(false);

  // Countdown timer for resend cooldown
  React.useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const phoneForm = useForm({
    resolver: zodResolver(PhoneOtpVerificationSchema),
    defaultValues: {
      phone: props.defaultPhone || '',
      token: '',
    },
  });

  // Shared post-auth navigation logic used by BOTH:
  //   - handleVerifyOtp (sign-in path: redirects immediately after OTP)
  //   - handleProfileComplete (sign-up path: redirects after profile step)
  const navigateAfterAuth = React.useCallback(async () => {
    try {
      const assuranceLevel = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

      if (!assuranceLevel.error) {
        const { nextLevel, currentLevel } = assuranceLevel.data;
        const requiresMfa = nextLevel === ASSURANCE_LEVEL_2 && nextLevel !== currentLevel;

        const inviteToken = props.inviteToken;
        const next = shouldCreateUser
          ? signupProfileSetupPath(params.get('next'))
          : (params.get('next') ?? '/auth/profile-setup');

        if (requiresMfa) {
          const nextPath = inviteToken
            ? `/join?invite_token=${inviteToken}&next=${encodeURIComponent(next)}`
            : next;

          window.location.href = `/auth/verify?next=${encodeURIComponent(nextPath)}`;
          return;
        }

        if (inviteToken) {
          const searchParams = new URLSearchParams({
            invite_token: inviteToken,
            next,
          });

          window.location.href = `/join?${searchParams.toString()}`;
        } else {
          window.location.href = next;
        }
      }
    } catch {
      // MFA check failed — fall back to the same destination. The server-side
      // middleware will enforce MFA compliance on the next page load if required.
      const inviteToken = props.inviteToken;
      const next = shouldCreateUser
        ? signupProfileSetupPath(params.get('next'))
        : (params.get('next') ?? '/auth/profile-setup');

      if (inviteToken) {
        const searchParams = new URLSearchParams({
          invite_token: inviteToken,
          next,
        });

        window.location.href = `/join?${searchParams.toString()}`;
      } else {
        window.location.href = next;
      }
    }
  }, [supabase, props.inviteToken, params, shouldCreateUser]);

  const handleVerifyOtp = async ({
    token,
    phone,
  }: {
    token: string;
    phone: string;
  }) => {
    await verifyMutation.mutateAsync({
      phone,
      token,
      type: 'sms',
    });

    // Record successful phone auth
    recordAuthMethod('otp', { email: phone });

    // Associate customer with tenant (non-critical, runs in background)
    associateCustomerWithTenantAction().catch(() => {});

    if (shouldCreateUser) {
      // Sign-up: go to profile step instead of redirecting
      setVerifiedPhone(phone);
      setStep('profile');
    } else {
      // Sign-in: navigate immediately
      await navigateAfterAuth();
    }
  };

  if (step === 'phone') {
    return (
      <>
        <PhoneNumberForm
          shouldCreateUser={shouldCreateUser}
          defaultPhone={props.defaultPhone ?? undefined}
          signUpPath={props.signUpPath ?? '/auth/sign-up'}
          displayTermsCheckbox={props.displayTermsCheckbox}
          tenantSlug={props.tenantSlug}
          onSwitchToEmail={props.onSwitchToEmail}
          autoSentRef={autoSentRef}
          onSendOtp={(phone, mirror) => {
            phoneForm.setValue('phone', phone, { shouldValidate: true });
            setOtpEmailMirror(Boolean(mirror));
            setStep('otp');
            setResendCooldown(30);
          }}
        />

        {props.footer}
      </>
    );
  }

  if (step === 'verifyEmail' && pendingVerifyEmail) {
    return (
      <SignupEmailVerifyStep
        email={pendingVerifyEmail}
        onDone={navigateAfterAuth}
      />
    );
  }

  if (step === 'profile') {
    return (
      <PhoneSignupProfileForm
        defaultName={props.defaultName}
        emailHint={props.emailHint}
        displayChildrenSection={props.displayChildrenSection}
        signupCustomFields={props.signupCustomFields}
        verifiedPhone={verifiedPhone}
        tenantSlug={props.tenantSlug}
        onComplete={navigateAfterAuth}
        onEmailStored={(email) => {
          setPendingVerifyEmail(email);
          setStep('verifyEmail');
        }}
        onError={(code) => {
          if (code === 'user_exists') {
            toast.error(t('auth:errors.duplicateEmailError'));
          } else {
            toast.error(t('auth:errors.generic'));
          }
        }}
      />
    );
  }

  // OTP step
  const phone = phoneForm.getValues('phone');

  return (
    <Form {...phoneForm}>
      <form
        className="flex w-full flex-col items-center space-y-8"
        onSubmit={phoneForm.handleSubmit(handleVerifyOtp)}
      >
        <div className="text-center">
          <p className="text-muted-foreground text-sm">
            <Trans
              i18nKey={
                otpEmailMirror
                  ? 'auth:phoneOtpSentToPhoneAndEmail'
                  : 'auth:phoneOtpSentToPhone'
              }
              values={{ phone: formatPhoneNumber(phone) }}
            />
          </p>
        </div>

        <AuthErrorAlert error={verifyMutation.error} />

        <FormField
          name="token"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputOTP
                  maxLength={6}
                  {...field}
                  disabled={verifyMutation.isPending}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} data-slot="0" />
                    <InputOTPSlot index={1} data-slot="1" />
                    <InputOTPSlot index={2} data-slot="2" />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} data-slot="3" />
                    <InputOTPSlot index={4} data-slot="4" />
                    <InputOTPSlot index={5} data-slot="5" />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>

              <FormDescription>
                <Trans i18nKey="auth:enterCodeFromPhone" />
              </FormDescription>

              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex w-full flex-col gap-y-2">
          <Button
            type="submit"
            disabled={verifyMutation.isPending}
            data-test="phone-otp-verify-button"
          >
            {verifyMutation.isPending ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                <Trans i18nKey="auth:verifyingCode" />
              </>
            ) : (
              <Trans i18nKey="auth:verifyCode" />
            )}
          </Button>

          <Button
            type="button"
            variant="ghost"
            disabled={verifyMutation.isPending || resendCooldown > 0}
            onClick={() => {
              setStep('phone');
            }}
          >
            {resendCooldown > 0 ? (
              <Trans
                i18nKey="auth:resendCodeIn"
                values={{ seconds: resendCooldown }}
              />
            ) : (
              <Trans i18nKey="auth:requestNewCode" />
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}

// ---------------------------------------------------------------------------
// PhoneNumberForm — Step 1: phone number only (both sign-up and sign-in)
// ---------------------------------------------------------------------------

function PhoneNumberForm({
  shouldCreateUser,
  defaultPhone,
  signUpPath,
  displayTermsCheckbox,
  tenantSlug,
  onSwitchToEmail,
  autoSentRef,
  onSendOtp,
}: {
  shouldCreateUser: boolean;
  defaultPhone?: string;
  signUpPath: string;
  displayTermsCheckbox?: boolean;
  tenantSlug?: string;
  onSwitchToEmail?: () => void;
  // Container-owned one-shot guard for the guest-upgrade auto-send (survives
  // this form's remounts; see PhoneSignInContainer).
  autoSentRef: React.MutableRefObject<boolean>;
  // `otpEmailMirror` comes from the identify pre-check (sign-in only): true
  // when the code will also land in the account's VERIFIED email.
  onSendOtp: (phone: string, otpEmailMirror?: boolean) => void;
}) {
  const captcha = useCaptcha();
  const prepareAndSendOtp = useMutation({
    mutationFn: prepareAndSendPhoneOtpAction,
  });
  const monitoring = useMonitoring();
  const router = useRouter();
  const { t } = useTranslation();
  const [identifying, startIdentify] = React.useTransition();
  // Sign-in only: an 'unknown' identify result renders an inline choice card
  // instead of auto-bouncing to sign-up (parity with the native sign-in
  // page). The user may have signed up with email — offer that path before
  // pushing them into creating a duplicate account.
  const [unknownPhone, setUnknownPhone] = React.useState<string | null>(null);

  const phoneForm = useForm({
    resolver: zodResolver(PhoneSignInSchema),
    mode: 'onChange',
    defaultValues: {
      phone: defaultPhone ?? '',
    },
  });

  const routeToSignUp = React.useCallback(
    (phone: string, hint?: IdentifyPhoneResult) => {
      const qs = new URLSearchParams({ phone });
      if (hint?.kind === 'guest_upgrade') {
        if (hint.name) qs.set('name', hint.name);
        if (hint.emailHint) qs.set('email_hint', hint.emailHint);
      }
      router.push(`${signUpPath}?${qs.toString()}`);
    },
    [router, signUpPath],
  );

  const sendOtp = React.useCallback(
    async (phone: string, otpEmailMirror?: boolean) => {
      try {
        // SINGLE send chokepoint for multi-tenant correctness. Every phone OTP
        // goes through prepareAndSendPhoneOtpAction, which resolves the tenant
        // from the request-bound page slug and merges it into the (shared)
        // user_metadata BEFORE the send. We never let GoTrue's signInWithOtp run
        // on its own here, because it drops options.data for existing users and
        // the Send-SMS hook would then fall back to whatever stale tenant_slug is
        // persisted on the shared account row (the cause of cross-tenant "wrong
        // brand / 500" failures). When there's no page tenant context we fall
        // back to NEXT_PUBLIC_DEFAULT_LANDING_PAGE (the platform's own tenant);
        // the action fails closed if even that doesn't resolve, so an OTP is
        // never sent without a real tenant.
        const resolvedTenantSlug =
          tenantSlug || process.env.NEXT_PUBLIC_DEFAULT_LANDING_PAGE;

        if (!resolvedTenantSlug) {
          throw new Error('missing_tenant_context');
        }

        const result = await prepareAndSendOtp.mutateAsync({
          phone,
          tenantSlug: resolvedTenantSlug,
          captchaToken: captcha.token,
          shouldCreateUser,
          // Sign-up: stamp guest role so abandoned profiles don't look like full accounts
          ...(shouldCreateUser ? { signupRole: 'guest' } : {}),
        });

        if (!result.success) {
          throw new Error(result.error);
        }

        captcha.reset();
        onSendOtp(phone, otpEmailMirror);
      } catch (error) {
        captcha.reset();

        const errorMessage =
          typeof error === 'string' ? error : (error as Error)?.message || '';
        const isDuplicateEmailError =
          errorMessage.includes('duplicate key value violates unique constraint') ||
          errorMessage.includes('accounts_email_key') ||
          errorMessage.includes('already exists') ||
          errorMessage.includes('Database error saving new user');
        const isCaptchaError = errorMessage.toLowerCase().includes('captcha');

        monitoring.captureException(
          error instanceof Error
            ? error
            : new Error(errorMessage || 'Phone sign-in failed'),
          {
            extra: {
              errorType: isCaptchaError
                ? 'captcha_verification_failed'
                : 'phone_sign_in_failed',
              authMethod: 'phone',
              shouldCreateUser,
            },
          },
        );

        if (isDuplicateEmailError && shouldCreateUser) {
          toast.error(t('auth:errors.duplicateEmailError'));
          return;
        }

        throw error;
      }
    },
    [
      captcha,
      monitoring,
      onSendOtp,
      shouldCreateUser,
      prepareAndSendOtp,
      tenantSlug,
      t,
    ],
  );

  const handleSendOtp = async (data: { phone: string }) => {
    const sendOtpWithToast = async (otpEmailMirror?: boolean) => {
      try {
        await sendOtp(data.phone, otpEmailMirror);
      } catch {
        toast.error(t('auth:errors.generic'));
      }
    };

    if (shouldCreateUser) {
      // Sign-up: identify first so we never send a sign-up OTP to a phone that
      // already belongs to a FULL account. Without this, signInWithOtp(
      // shouldCreateUser:true) would sign whoever enters the code into the
      // existing user, and the profile step would clobber their account.
      // 'exists' → bounce to sign-in; guest_upgrade/unknown → proceed (the guest
      // gets upgraded, an unknown phone creates a fresh guest). On identify
      // failure we fall through to send — the server-side takeover guard in
      // completePhoneSignupProfileAction is the backstop.
      startIdentify(async () => {
        try {
          const result = await identifyAccountByPhoneAction({
            phone: data.phone,
          });

          if (result.kind === 'exists') {
            // Not a dead end: this account already exists, so take the user
            // to sign-in (phone method available there) with a plain-text
            // toast explaining why. The linked-copy variant of this error is
            // reserved for AuthErrorAlert, which can render the tags.
            toast.error(
              t(
                'auth:errors.phoneAlreadyRegisteredPlain',
                'This phone number is already registered — sign in instead.',
              ),
            );
            router.push('/auth/sign-in');
            return;
          }
        } catch (error) {
          monitoring.captureException(
            error instanceof Error
              ? error
              : new Error('Identify phone failed (signup)'),
          );
        }

        await sendOtpWithToast();
      });
      return;
    }

    // Sign-in: identify first so we can route unknown / guest-upgrade phones
    setUnknownPhone(null);
    startIdentify(async () => {
      try {
        const result = await identifyAccountByPhoneAction({ phone: data.phone });

        if (result.kind === 'guest_upgrade') {
          // Working upgrade path — keep the auto-route.
          routeToSignUp(data.phone, result);
          return;
        }

        if (result.kind === 'unknown') {
          // No hard bounce to sign-up: offer "try email instead" (they may
          // have signed up on the other channel) alongside "create account".
          setUnknownPhone(data.phone);
          return;
        }

        await sendOtpWithToast(result.otpEmailMirror);
      } catch (error) {
        monitoring.captureException(
          error instanceof Error ? error : new Error('Identify phone failed'),
        );
        await sendOtpWithToast();
      }
    });
  };

  // Auto-advance: when a sign-up arrives from a guest_upgrade with the phone
  // already known (?phone=… in the URL), we already identified the account
  // upstream — re-asking for the phone is redundant. Send the OTP once and jump
  // straight to the code step.
  //
  // The guard (autoSentRef) is CONTAINER-owned so it survives this form's
  // remounts — otherwise "Request new code" (which remounts us via setStep) would
  // auto-send again, bypassing the resend cooldown and double-sending. We also
  // seed the form field with the known phone up front: react-phone-number-input
  // doesn't reliably hydrate a controlled value whose country differs from the
  // tenant default, so without seeding a FAILED auto-send would strand the user
  // on a blank field. On failure we clear the guard so a manual retry works.
  React.useEffect(() => {
    if (autoSentRef.current) return;
    if (!shouldCreateUser) return;
    if (!defaultPhone || !isValidPhoneNumber(defaultPhone)) return;
    if (!captcha.isReady) return;

    autoSentRef.current = true;
    // Seed the field so the user isn't left looking at a blank input if the
    // send fails and they fall back to the manual form.
    phoneForm.setValue('phone', defaultPhone, { shouldValidate: true });

    void handleSendOtp({ phone: defaultPhone });
    // One-shot per container; deps kept minimal so a captcha token refresh can't
    // re-fire it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCreateUser, defaultPhone, captcha.isReady]);

  // If the auto-send fails the form stays mounted in an error state — clear the
  // one-shot guard so the user's manual retry (or a remount) can send again.
  // Race-free: keyed on the mutation's settled error state, not a closure value.
  React.useEffect(() => {
    if (prepareAndSendOtp.isError) {
      autoSentRef.current = false;
    }
  }, [prepareAndSendOtp.isError, autoSentRef]);

  return (
    <Form {...phoneForm}>
      <form
        className="flex flex-col gap-y-4"
        onSubmit={phoneForm.handleSubmit(handleSendOtp)}
      >
        {unknownPhone ? (
          <UnknownAccountCard
            channel="phone"
            onTryOtherMethod={onSwitchToEmail}
            onCreateAccount={() => routeToSignUp(unknownPhone)}
            onDismiss={() => setUnknownPhone(null)}
          />
        ) : null}

        <FormField
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <PhoneInput
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={prepareAndSendOtp.isPending}
                  placeholder="Enter phone number"
                  data-test="phone-input"
                />
              </FormControl>

              <FormDescription>
                <Trans i18nKey="auth:phoneNumberWithCountryCode" />
              </FormDescription>
            </FormItem>
          )}
        />

        {captcha.field}

        <Button
          type="submit"
          disabled={
            prepareAndSendOtp.isPending ||
            identifying ||
            !phoneForm.formState.isValid
          }
          data-test="phone-send-otp-button"
          className={!phoneForm.formState.isValid ? 'cursor-not-allowed opacity-50' : ''}
        >
          {prepareAndSendOtp.isPending || identifying ? (
            <>
              <Spinner className="mr-2 h-4 w-4" />
              <Trans
                i18nKey={
                  identifying ? 'auth:checkingAccount' : 'auth:sendingCode'
                }
              />
            </>
          ) : (
            <Trans i18nKey="auth:sendVerificationCode" />
          )}
        </Button>

        {shouldCreateUser && displayTermsCheckbox && (
          <TermsAndConditionsFormField />
        )}
      </form>
    </Form>
  );
}

// ---------------------------------------------------------------------------
// PhoneSignupProfileForm — Step 3 (sign-up only): name + email + children
// ---------------------------------------------------------------------------

const PhoneSignupProfileWithChildrenSchema = PhoneSignupProfileSchema.extend({
  children: z
    .array(
      z.object({
        firstName: z
          .string()
          .min(2, 'First name must be at least 2 characters')
          .max(50),
        lastName: z
          .string()
          .min(2, 'Last name must be at least 2 characters')
          .max(50),
      }),
    )
    .max(MAX_CHILDREN)
    .optional()
    .default([]),
});

type PhoneSignupProfileFormValues = z.infer<
  typeof PhoneSignupProfileWithChildrenSchema
>;

// Email-mismatch banner shown above the profile form when the server reports
// that the typed email differs from the one already on the account (guest_upgrade).
function EmailMismatchBanner({
  emailHint,
  typedEmail,
  loading,
  onUseExisting,
  onOverride,
  onDismiss,
}: {
  emailHint: string;
  typedEmail: string;
  loading: boolean;
  onUseExisting: () => void;
  onOverride: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm">
      <p className="font-medium text-foreground">
        <Trans i18nKey="auth:emailMismatchHeading" defaults="Is this you?" />
      </p>

      <p className="text-muted-foreground mt-1">
        <Trans
          i18nKey="auth:emailMismatchBody"
          values={{ emailHint, typedEmail }}
          components={{ strong: <strong className="text-foreground" /> }}
          defaults="We already have a profile on this phone number linked to <strong>{{emailHint}}</strong>. You typed <strong>{{typedEmail}}</strong>. Which email do you want to use?"
        />
      </p>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          disabled={loading}
          onClick={onUseExisting}
          className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          data-test="email-mismatch-use-existing"
        >
          <Trans
            i18nKey="auth:emailMismatchUseExisting"
            defaults="Use the email on file"
          />
        </button>

        <button
          type="button"
          disabled={loading}
          onClick={onOverride}
          className="inline-flex items-center justify-center rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
          data-test="email-mismatch-override"
        >
          <Trans
            i18nKey="auth:emailMismatchOverride"
            defaults="No, use the new email"
          />
        </button>

        <button
          type="button"
          disabled={loading}
          onClick={onDismiss}
          className="inline-flex items-center justify-center rounded-md px-3 py-2 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          <Trans i18nKey="common:cancel" defaults="Cancel" />
        </button>
      </div>
    </div>
  );
}

function PhoneSignupProfileForm({
  defaultName,
  emailHint,
  displayChildrenSection = true,
  signupCustomFields = [],
  verifiedPhone: _verifiedPhone,
  tenantSlug,
  onComplete,
  onEmailStored,
  onError,
}: {
  defaultName?: string;
  emailHint?: string;
  signupCustomFields?: SignupCustomField[];
  displayChildrenSection?: boolean;
  verifiedPhone: string;
  tenantSlug?: string;
  onComplete: () => Promise<void>;
  // Called instead of onComplete when the profile stored a NEW (unverified)
  // email — the container then offers the skippable verify step.
  onEmailStored: (email: string) => void;
  onError: (code: 'user_exists' | 'generic') => void;
}) {
  const { t } = useTranslation();
  const [submitting, startSubmit] = React.useTransition();
  const [childrenOpen, setChildrenOpen] = React.useState(false);

  // Email mismatch banner state: null = no mismatch; object = mismatch active
  const [mismatch, setMismatch] = React.useState<{
    emailHint: string;
    typedEmail: string;
  } | null>(null);

  // 'onTouched': errors surface only after the user leaves a field (then live
  // while they fix it) — no red text while they're still typing a first pass.
  // formState.isValid stays live regardless of mode (RHF revalidates the full
  // schema in the background when isValid is subscribed), so the submit
  // button's disabled state is unaffected.
  const profileForm = useForm<PhoneSignupProfileFormValues>({
    resolver: zodResolver(
      PhoneSignupProfileWithChildrenSchema.superRefine(
        refineSignupFields(signupCustomFields),
      ),
    ),
    mode: 'onTouched',
    defaultValues: {
      name: defaultName ?? '',
      email: '',
      children: [],
    },
  });

  // What still blocks submission — drives the tooltip on the disabled submit
  // button (desktop hover). Derived from the schema on current values (not
  // formState.errors) so untouched-but-empty fields are included too.
  const watchedValues = profileForm.watch();
  const missingRequirements = React.useMemo(() => {
    const parsed =
      PhoneSignupProfileWithChildrenSchema.safeParse(watchedValues);
    if (parsed.success) return [];

    const lines: string[] = [];
    const seenRoots = new Set<string>();

    for (const issue of parsed.error.issues) {
      const root = String(issue.path[0] ?? '');
      if (seenRoots.has(root)) continue;
      seenRoots.add(root);

      if (root === 'name') {
        lines.push(
          t('auth:nameMustBeFullName', 'Please enter your first and last name.'),
        );
      } else if (root === 'email') {
        lines.push(
          t('auth:phoneProfileEmailHintInvalid', 'Enter a valid email address.'),
        );
      } else if (root === 'children') {
        lines.push(
          t(
            'auth:childrenNamesIncomplete',
            "Complete each child's first and last name.",
          ),
        );
      }
    }

    return lines;
  }, [watchedValues, t]);

  const { fields, append, remove } = useFieldArray({
    control: profileForm.control,
    name: 'children' as never,
  });

  const handleToggleChildren = () => {
    if (!childrenOpen) {
      setChildrenOpen(true);
      if (fields.length === 0) {
        append({ firstName: '', lastName: '' } as never);
      }
    } else {
      setChildrenOpen(false);
    }
  };

  const handleAddChild = () => {
    if (fields.length < MAX_CHILDREN) {
      append({ firstName: '', lastName: '' } as never);
    }
  };

  const handleRemoveChild = (index: number) => {
    remove(index);
    if (fields.length <= 1) {
      setChildrenOpen(false);
    }
  };

  const submitProfile = React.useCallback(
    async (
      data: PhoneSignupProfileFormValues,
      emailConflictResolution?: 'use-existing' | 'override',
    ) => {
      const result = await completePhoneSignupProfileAction({
        name: data.name,
        email: data.email,
        children: data.children,
        signupFields: data.signupFields,
        emailConflictResolution,
        // Same fallback idiom as sendOtp: header-less hosts (localhost, main
        // domain) resolve the default landing tenant server-side.
        tenantSlug:
          tenantSlug || process.env.NEXT_PUBLIC_DEFAULT_LANDING_PAGE,
      });

      if (result.success) {
        setMismatch(null);

        // A newly stored email is unverified — offer the skippable verify
        // step. 'use-existing' keeps the on-file address (we only hold its
        // masked hint, so there's nothing to send a code to from here — the
        // profile page covers it).
        if (data.email && emailConflictResolution !== 'use-existing') {
          onEmailStored(data.email);
          return;
        }

        await onComplete();
        return;
      }

      // Session is an already-complete account (the phone matched a full user).
      // We're signed in as them — don't write anything, just proceed.
      if ('code' in result && result.code === 'already_complete') {
        setMismatch(null);
        await onComplete();
        return;
      }

      if ('code' in result && result.code === 'email_mismatch') {
        setMismatch({ emailHint: result.emailHint, typedEmail: data.email });
        return;
      }

      if ('code' in result && result.code === 'user_exists') {
        onError('user_exists');
        return;
      }

      // Generic error
      onError('generic');
    },
    [onComplete, onEmailStored, onError, tenantSlug],
  );

  const handleSubmit = (data: PhoneSignupProfileFormValues) => {
    startSubmit(async () => {
      await submitProfile(data);
    });
  };

  const handleUseExisting = () => {
    startSubmit(async () => {
      await submitProfile(profileForm.getValues(), 'use-existing');
    });
  };

  const handleOverride = () => {
    startSubmit(async () => {
      await submitProfile(profileForm.getValues(), 'override');
    });
  };

  const handleDismissMismatch = () => {
    setMismatch(null);
  };

  return (
    <div className="flex flex-col gap-y-4">
      {mismatch ? (
        <EmailMismatchBanner
          emailHint={mismatch.emailHint}
          typedEmail={mismatch.typedEmail}
          loading={submitting}
          onUseExisting={handleUseExisting}
          onOverride={handleOverride}
          onDismiss={handleDismissMismatch}
        />
      ) : null}

      {emailHint && !mismatch ? (
        <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-800 border border-blue-200">
          <Trans
            i18nKey="auth:emailOnFileHint"
            defaults="We have {{emailHint}} on file for this phone — use that email below to keep your existing data."
            values={{ emailHint }}
          />
        </div>
      ) : null}

      <Form {...profileForm}>
        <form
          className="flex flex-col gap-y-4"
          onSubmit={profileForm.handleSubmit(handleSubmit)}
        >
          <FormField
            name="name"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <Input
                    required
                    type="text"
                    placeholder={t('auth:fullNamePlaceholder', 'Enter your full name')}
                    data-test="phone-profile-name-input"
                    {...field}
                  />
                </FormControl>

                {/* Red error appears on blur (mode: 'onTouched'); the gray
                    helper hides while an error is showing to avoid stacking. */}
                <FormMessage />

                {!fieldState.error ? (
                  <FormDescription>
                    <Trans i18nKey="auth:nameRequired" />
                  </FormDescription>
                ) : null}
              </FormItem>
            )}
          />

          <FormField
            name="email"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t('common:emailAddress', 'Email')}
                    data-test="phone-profile-email-input"
                    {...field}
                  />
                </FormControl>

                <FormMessage />

                {!fieldState.error ? (
                  <FormDescription>
                    <Trans
                      i18nKey="auth:phoneProfileEmailHint"
                      defaults="We'll use this for receipts and account recovery"
                    />
                  </FormDescription>
                ) : null}
              </FormItem>
            )}
          />

          <SignupCustomFieldsInput
            fields={signupCustomFields}
            disabled={submitting}
          />

          {/* Children Section */}
          {displayChildrenSection && (
            <div>
              <button
                type="button"
                onClick={handleToggleChildren}
                className={
                  'flex w-full items-center gap-3 rounded-lg border border-dashed px-3 py-2.5 text-left transition-colors' +
                  (childrenOpen
                    ? ' border-primary/30 bg-primary/5'
                    : ' border-muted-foreground/25 hover:border-primary/30 hover:bg-primary/5')
                }
              >
                <div className="bg-primary/10 flex h-7 w-7 shrink-0 items-center justify-center rounded-md">
                  <Plus
                    className={
                      'text-primary h-3.5 w-3.5 transition-transform' +
                      (childrenOpen ? ' rotate-45' : '')
                    }
                  />
                </div>

                <div className="flex-1">
                  <span className="text-primary text-sm font-medium">
                    <Trans
                      i18nKey="auth:registeringChildren"
                      defaults="Registering children?"
                    />
                  </span>

                  {childrenOpen && fields.length > 0 && (
                    <span className="text-muted-foreground block text-xs">
                      <Trans
                        i18nKey="auth:childrenCount"
                        defaults="{{count}} children registered"
                        values={{ count: fields.length }}
                      />
                    </span>
                  )}
                </div>

                <ChevronDown
                  className={
                    'text-muted-foreground h-4 w-4 shrink-0 transition-transform' +
                    (childrenOpen ? ' rotate-180' : '')
                  }
                />
              </button>

              {childrenOpen && (
                <div className="bg-primary/5 border-primary/30 rounded-b-lg border border-t-0 border-dashed p-3">
                  <p className="text-muted-foreground mb-3 text-xs">
                    <Trans
                      i18nKey="auth:childrenHelperText"
                      defaults="Add your children's names so you can book sessions for them later."
                    />
                  </p>

                  <div className="space-y-2">
                    {fields.map((field, index) => (
                      <div key={field.id} className="flex items-start gap-2">
                        <span className="border-border bg-background text-muted-foreground mt-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium">
                          {index + 1}
                        </span>

                        <div className="flex flex-1 gap-2">
                          <FormField
                            control={profileForm.control}
                            name={`children.${index}.firstName` as never}
                            render={({ field: childField }) => (
                              <FormItem className="flex-1">
                                <FormControl>
                                  <Input
                                    placeholder="First name"
                                    className="h-9 text-sm"
                                    data-test={`child-first-name-${index}`}
                                    {...childField}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={profileForm.control}
                            name={`children.${index}.lastName` as never}
                            render={({ field: childField }) => (
                              <FormItem className="flex-1">
                                <FormControl>
                                  <Input
                                    placeholder="Last name"
                                    className="h-9 text-sm"
                                    data-test={`child-last-name-${index}`}
                                    {...childField}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <button
                          type="button"
                          onClick={() => handleRemoveChild(index)}
                          className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 mt-1.5 rounded-md p-1.5 transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={handleAddChild}
                    disabled={fields.length >= MAX_CHILDREN}
                    className="text-primary hover:text-primary/80 mt-2 flex items-center gap-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <Trans
                      i18nKey="auth:addAnotherChild"
                      defaults="Add another child"
                    />
                  </button>
                </div>
              )}
            </div>
          )}

          {missingRequirements.length > 0 && !submitting ? (
            // Disabled state: hovering the button explains what's still
            // missing (desktop). Disabled buttons swallow pointer events, so
            // the span carries the tooltip trigger and the button is made
            // pointer-transparent. On touch devices the on-blur field errors
            // carry this information instead.
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="w-full">
                    <Button
                      type="submit"
                      disabled
                      className="pointer-events-none w-full"
                      data-test="phone-profile-submit-button"
                    >
                      <Trans
                        i18nKey="auth:completeProfile"
                        defaults="Complete sign up"
                      />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="font-medium">
                    <Trans
                      i18nKey="auth:completeProfileMissing"
                      defaults="To finish signing up:"
                    />
                  </p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-4">
                    {missingRequirements.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Button
              type="submit"
              disabled={submitting}
              data-test="phone-profile-submit-button"
            >
              {submitting ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  <Trans i18nKey="auth:savingProfile" defaults="Saving..." />
                </>
              ) : (
                <Trans i18nKey="auth:completeProfile" defaults="Complete sign up" />
              )}
            </Button>
          )}
        </form>
      </Form>
    </div>
  );
}
